const WINBACK_CHECKOUT_STATES = {
  OFFER: 'offer',
  EXPIRED: 'expired',
  ALREADY_SUBSCRIBED: 'already_subscribed',
  ERROR: 'error',
};

const WINBACK_STANDARD_YEARLY_PRICE = '$39.99';

const WINBACK_OFFER_TIERS = {
  1: { firstYearPrice: '$29.99', discountLabel: '$10 off your first year' },
  2: { firstYearPrice: '$19.99', discountLabel: 'Half off your first year' },
};

const getWinbackCheckoutCtaLabel = (discountLabel) => {
  const cleaned = String(discountLabel || '').trim();
  if (/half\s+off/i.test(cleaned)) {
    return 'Get 50% off annual';
  }

  const dollarMatch = cleaned.match(/\$\d+(?:\.\d{2})?\s*off/i);
  if (dollarMatch) {
    return `Get ${dollarMatch[0].replace(/\s+/g, ' ')} annual`;
  }

  return 'Continue to annual plan';
};

const getWinbackCheckoutParams = (search) => {
  const params = new URLSearchParams(search || '');
  return {
    token: String(params.get('token') || '').trim(),
    canceled: params.get('canceled') === '1',
  };
};

const buildWinbackRedirectUrls = (origin, token) => {
  const cleanedOrigin = String(origin || '').replace(/\/$/, '');
  const tokenParam = encodeURIComponent(token || '');
  return {
    successUrl: `${cleanedOrigin}/checkout/success?token=${tokenParam}`,
    cancelUrl: `${cleanedOrigin}/checkout?token=${tokenParam}&canceled=1`,
  };
};

const decodeBase64Url = (value) => {
  const normalized = String(value || '').replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  if (typeof atob === 'function') {
    return atob(padded);
  }
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(padded, 'base64').toString('binary');
  }
  throw new Error('No base64 decoder available.');
};

// The win-back token is b64url(payloadJSON).b64url(signature). The payload is
// display-only on the client ({"u": uid, "t": tier, "e": expiry epoch seconds});
// we cannot verify the signature here, and the server re-validates on POST.
const decodeWinbackTokenPayload = (token) => {
  const payloadSegment = String(token || '').split('.')[0];
  if (!payloadSegment) {
    return null;
  }

  let parsed = null;
  try {
    parsed = JSON.parse(decodeBase64Url(payloadSegment));
  } catch (decodeError) {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return null;
  }

  return {
    uid: typeof parsed.u === 'string' ? parsed.u : '',
    tier: parsed.t === 1 || parsed.t === 2 ? parsed.t : null,
    expiresAtMs:
      typeof parsed.e === 'number' && Number.isFinite(parsed.e) ? parsed.e * 1000 : null,
  };
};

const getWinbackOfferDetails = (tier) => WINBACK_OFFER_TIERS[tier] || null;

// Mirrors the post-subscribe reveal screen header in the mobile app.
const getWinbackHeadline = (petName) => {
  const name = String(petName || '').trim();
  return name ? `Meet newborn ${name}` : 'Meet your newborn pet';
};

const getWinbackSubline = (pronounObject) => {
  const pronoun = String(pronounObject || '').trim() || 'them';
  return `Meet ${pronoun} as a newborn and watch ${pronoun} grow, week by week`;
};

const withDollar = (amount) => {
  const cleaned = String(amount == null ? '' : amount).trim();
  if (!cleaned) {
    return '';
  }
  return cleaned.startsWith('$') ? cleaned : `$${cleaned}`;
};

// Normalizes the /winback_offer_details response into a display view model,
// tolerating missing fields so a partial response still renders the paywall.
const normalizeWinbackOfferDetails = (response) => {
  const data = response && typeof response === 'object' ? response : {};
  const pet = data.pet && typeof data.pet === 'object' ? data.pet : {};
  const prices = data.prices && typeof data.prices === 'object' ? data.prices : {};
  const media = data.media && typeof data.media === 'object' ? data.media : {};
  const support = data.support && typeof data.support === 'object' ? data.support : {};
  const tier = data.tier === 1 || data.tier === 2 ? data.tier : null;

  const firstYearPrice = withDollar(prices.first_year) || (getWinbackOfferDetails(tier)?.firstYearPrice || '');
  const standardYearlyPrice = withDollar(prices.standard_yearly) || WINBACK_STANDARD_YEARLY_PRICE;
  const mediaKind = ['gif', 'image', 'video'].includes(media.kind) ? media.kind : null;

  return {
    tier,
    petName: typeof pet.name === 'string' ? pet.name : '',
    pronounObject: typeof pet.pronoun_object === 'string' && pet.pronoun_object ? pet.pronoun_object : 'them',
    petType: pet.type === 'cat' ? 'cat' : 'dog',
    babyWord: typeof pet.baby_word === 'string' && pet.baby_word ? pet.baby_word : 'puppy',
    firstYearPrice,
    standardYearlyPrice,
    discountLabel: getWinbackOfferDetails(tier)?.discountLabel || '',
    media: mediaKind ? { kind: mediaKind, url: media.url || '', posterUrl: media.poster_url || '' } : null,
    sounds: Array.isArray(data.sounds) ? data.sounds.filter((url) => typeof url === 'string' && url) : [],
    support: {
      firebaseUid: typeof support.firebase_uid === 'string' ? support.firebase_uid : '',
      email: typeof support.email === 'string' ? support.email : '',
    },
  };
};

const isWinbackPayloadExpired = (payload, nowMs = Date.now()) =>
  Boolean(payload && typeof payload.expiresAtMs === 'number' && payload.expiresAtMs <= nowMs);

const resolveWinbackErrorState = (error) => {
  const code = String(error?.code || '').trim().toLowerCase();
  if (code === 'invalid_token') {
    return WINBACK_CHECKOUT_STATES.EXPIRED;
  }
  if (code === 'already_subscribed') {
    return WINBACK_CHECKOUT_STATES.ALREADY_SUBSCRIBED;
  }
  return WINBACK_CHECKOUT_STATES.ERROR;
};

const getInitialWinbackCheckoutState = ({ token, payload, nowMs = Date.now() }) => {
  if (!token) {
    return WINBACK_CHECKOUT_STATES.EXPIRED;
  }
  if (isWinbackPayloadExpired(payload, nowMs)) {
    return WINBACK_CHECKOUT_STATES.EXPIRED;
  }
  return WINBACK_CHECKOUT_STATES.OFFER;
};

export {
  WINBACK_CHECKOUT_STATES,
  WINBACK_STANDARD_YEARLY_PRICE,
  buildWinbackRedirectUrls,
  decodeWinbackTokenPayload,
  getWinbackCheckoutCtaLabel,
  getInitialWinbackCheckoutState,
  getWinbackCheckoutParams,
  getWinbackHeadline,
  getWinbackOfferDetails,
  getWinbackSubline,
  isWinbackPayloadExpired,
  normalizeWinbackOfferDetails,
  resolveWinbackErrorState,
};
